import React, { useState } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase/config';
import { DEFAULT_RESTAURANT_SETTINGS } from '../../types/restaurant';
import { AlertTriangle, CheckCircle, RefreshCw, ArrowLeft, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MigrationToolScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [migrated, setMigrated] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const migrateRestaurant = async (restaurantId: string): Promise<boolean> => {
    try {
      addLog(`Iniciando migración para: ${restaurantId}`);
      
      // Get current data
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      const snapshot = await getDoc(restaurantRef);
      
      if (!snapshot.exists()) {
        addLog(`❌ Restaurante ${restaurantId} no encontrado`);
        return false;
      }

      const oldData = snapshot.data() as any;
      addLog(`✓ Datos actuales obtenidos para: ${oldData.name || restaurantId}`);

      // Detect processor
      const processor = oldData.processor || 
        (oldData.stripeConfig ? 'stripe' :
         oldData.cardnetConfig ? 'cardnet' :
         oldData.azulConfig ? 'azul' : 'none');

      // Build new standardized structure
      const newData: any = {
        id: restaurantId,
        name: oldData.name || 'Unnamed Restaurant',
        active: oldData.active ?? true,
        processor,
        
        // POS Config
        posConfig: oldData.posConfig || {
          provider: 'mock',
          enabled: true,
          credentials: {},
          syncSettings: {
            autoSync: true,
            syncIntervalMinutes: 5,
          }
        },
        
        // Settings (merge old with defaults)
        settings: {
          ...DEFAULT_RESTAURANT_SETTINGS,
          ...oldData.settings,
          currency: oldData.settings?.currency || oldData.menuSettings?.currency || 'DOP',
          currencySymbol: oldData.settings?.currencySymbol || oldData.menuSettings?.currencySymbol || 'RD$',
          taxRate: oldData.settings?.taxRate ?? oldData.menuSettings?.taxRate ?? 18,
          serviceCharge: oldData.menuSettings?.serviceCharge ?? 0,
        },
        
        // Timestamps
        createdAt: oldData.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Add optional fields
      if (oldData.logoUrl) newData.logoUrl = oldData.logoUrl;
      if (oldData.address) newData.address = oldData.address;
      if (oldData.phone) newData.phone = oldData.phone;
      if (oldData.email) newData.email = oldData.email;

      // Add processor configs with updated structure
      if (processor === 'stripe' && oldData.stripeConfig) {
        newData.stripeConfig = {
          enabled: oldData.stripeConfig.enabled ?? true,
          environment: oldData.stripeConfig.environment || 'sandbox',
          publicKey: oldData.stripeConfig.publicKey || '',
          // Only add merchantId if it exists
          ...(oldData.stripeConfig.merchantId && { merchantId: oldData.stripeConfig.merchantId })
        };
      } else if (processor === 'cardnet' && oldData.cardnetConfig) {
        newData.cardnetConfig = {
          enabled: oldData.cardnetConfig.enabled ?? true,
          environment: oldData.cardnetConfig.environment || 'sandbox',
          merchantId: oldData.cardnetConfig.merchantId || '',
        };
      } else if (processor === 'azul' && oldData.azulConfig) {
        // Check if secrets already exist in GSM format
        if (oldData.azulConfig.secretName) {
          // Already migrated, keep as-is
          newData.azulConfig = oldData.azulConfig;
        } else {
          // Legacy format with auth1/auth2 in Firestore
          // Log warning - manual secret creation needed
          addLog(`⚠️ Restaurant ${restaurantId} has legacy Azul config. Create secret in GSM manually.`);
          
          newData.azulConfig = {
            enabled: oldData.azulConfig.enabled ?? true,
            environment: oldData.azulConfig.environment || 'sandbox',
            channel: oldData.azulConfig.channel || 'EC',
            merchantId: oldData.azulConfig.merchantId || '',
            secretName: '', // Placeholder - must be set manually
          };
        }
      }

      // Save updated structure
      await setDoc(restaurantRef, newData, { merge: false });
      addLog(`✅ Migrado exitosamente: ${newData.name}`);
      
      return true;
    } catch (error: any) {
      addLog(`❌ Error migrando ${restaurantId}: ${error.message}`);
      setErrors(prev => [...prev, `${restaurantId}: ${error.message}`]);
      return false;
    }
  };

  const handleMigrateAll = async () => {
    setLoading(true);
    setLogs([]);
    setMigrated([]);
    setErrors([]);

    addLog('🔄 Iniciando migración de todos los restaurantes...');

    try {
      const restaurantsRef = collection(db, 'restaurants');
      const snapshot = await getDocs(restaurantsRef);
      
      addLog(`Se encontraron ${snapshot.docs.length} restaurantes para migrar`);

      for (const docSnapshot of snapshot.docs) {
        const success = await migrateRestaurant(docSnapshot.id);
        if (success) {
          setMigrated(prev => [...prev, docSnapshot.id]);
        }
      }

      addLog('');
      addLog(`📊 Migración Completada:`);
      addLog(`   ✅ Éxitos: ${migrated.length}`);
      addLog(`   ❌ Errores: ${errors.length}`);

    } catch (error: any) {
      addLog(`❌ Error fatal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <button
        onClick={() => navigate('/super-admin')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver al Panel
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Herramienta de Migración</h1>
        <p className="text-gray-600 mt-2">
          Migrar restaurantes existentes al nuevo esquema estandarizado
        </p>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8 flex items-start gap-4">
        <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={24} />
        <div>
          <h3 className="font-bold text-yellow-900 mb-2">⚠️ Advertencia Importante</h3>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Esto ACTUALIZARÁ todos los documentos de restaurantes en Firestore</li>
            <li>• Asegúrate de tener una copia de seguridad antes de continuar</li>
            <li>• Los datos existentes se conservarán y se mejorarán con nuevos campos</li>
            <li>• Esta operación no se puede deshacer</li>
          </ul>
        </div>
      </div>

      {/* Action Button */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Ejecutar Migración</h2>
            <p className="text-gray-600 text-sm mt-1">
              Migrar todos los restaurantes al nuevo esquema
            </p>
          </div>
          <button
            onClick={handleMigrateAll}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Migrando...' : 'Iniciar Migración'}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {(migrated.length > 0 || errors.length > 0) && (
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="text-green-600" size={24} />
              <h3 className="font-bold text-green-900">Migrados Exitosamente</h3>
            </div>
            <p className="text-3xl font-bold text-green-600">{migrated.length}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="font-bold text-red-900">Errores</h3>
            </div>
            <p className="text-3xl font-bold text-red-600">{errors.length}</p>
          </div>
        </div>
      )}

      {/* Logs */}
      {logs.length > 0 && (
        <div className="bg-gray-900 text-green-400 rounded-xl p-6 font-mono text-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white">Registros de Migración</h3>
            <span className="text-gray-400">{logs.length} entradas</span>
          </div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {logs.map((log, index) => (
              <div key={index} className="whitespace-pre-wrap">
                {log}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {logs.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Database className="text-gray-400 mx-auto mb-4" size={48} />
          <p className="text-gray-600">
            Haz clic en "Iniciar Migración" para comenzar a migrar tus restaurantes
          </p>
        </div>
      )}
    </div>
  );
}